// @ts-check

import { render } from "preact";
import { html } from "htm/preact";
import { signal } from "@preact/signals";
import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import {
  cueSoundOptions,
  defaultCueSoundKey,
  normalizeCueSoundKey,
  playCueSound,
  playRepeatedCueSound,
  prepareCueSound,
} from "./audio.js";
import { setupServiceWorkerReloadWhenIdle } from "./sw-reload.js";
import {
  bindWakeLockLifecycle,
  disableWakeLock,
  enableWakeLock,
  getWakeLockDebugState,
} from "./wake_lock.js";
import {
  formatClock,
  getIntervalConfigFromParams,
  getIntervalSnapshot,
  getIntervalTotalSeconds,
  getRandomDelaySeconds,
  getReactionConfigFromParams,
  intervalDefaults,
  reactionDefaults,
  timerLimits,
} from "./timer_logic.js";

/**
 * @typedef {import("preact").ComponentChild} PreactView
 * @typedef {{ prompt: () => Promise<void> | void, userChoice: Promise<unknown> }} BeforeInstallPromptEvent
 * @typedef {{ name: "home" | "interval-setup" | "interval-run" | "reaction-setup" | "reaction-run" }} RouteState
 * @typedef {{ status: "running" | "paused" | "complete", startedAt: number, elapsedSeconds: number }} IntervalSession
 * @typedef {{ status: "running" | "paused", targetAt: number, remainingWhenPaused: number, cueVisibleUntil: number, cueCount: number }} ReactionSession
 * @typedef {ReturnType<typeof normalizeCueSoundKey>} CueSoundKey
 * @typedef {{ workSeconds: number, restSeconds: number, repetitions: number, workCueSoundKey: CueSoundKey, restCueSoundKey: CueSoundKey }} IntervalSettings
 * @typedef {{ minimumSeconds: number, maximumSeconds: number, cueSoundKey: CueSoundKey }} ReactionSettings
 */

const root = document.getElementById("app");
const routeEventName = "timer:navigate";
const historyDepthKey = "timerDepth";
const intervalStorageKey = "timer-interval-settings-v1";
const reactionStorageKey = "timer-reaction-settings-v1";
const cachePrefix = "timer-cache-";
const clockFrameIntervalMs = 50;
const reactionCueVisibleMs = 700;
const debugTapThreshold = 7;
const debugTapWindowMs = 1500;
const routeState = signal(getRouteStateFromLocation());
const installPromptState = signal(
  /** @type {BeforeInstallPromptEvent | null} */ (null),
);

let appBusy = false;
const debugTapState = { count: 0, lastTapAt: 0 };

function main() {
  if (!(root instanceof HTMLElement)) {
    return;
  }

  initializeHistoryState();
  render(html`<${App} />`, root);
}

function App() {
  const route = routeState.value;
  const installPrompt = installPromptState.value;

  useEffect(function bindAppLifecycle() {
    syncViewportHeight();
    void setupServiceWorkerReloadWhenIdle({ isIdle: isAppIdle });

    function handleRouteChange() {
      routeState.value = getRouteStateFromLocation();
      window.scrollTo(0, 0);
    }

    /** @param {Event} event */
    function handleBeforeInstallPrompt(event) {
      event.preventDefault();
      const promptEvent = /** @type {BeforeInstallPromptEvent} */ (
        /** @type {unknown} */ (event)
      );
      installPromptState.value = promptEvent;
    }

    function handleAppInstalled() {
      installPromptState.value = null;
    }

    window.addEventListener("popstate", handleRouteChange);
    window.addEventListener(routeEventName, handleRouteChange);
    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);
    window.addEventListener("resize", syncViewportHeight);

    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", syncViewportHeight);
    }

    return function cleanupAppLifecycle() {
      window.removeEventListener("popstate", handleRouteChange);
      window.removeEventListener(routeEventName, handleRouteChange);
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
      window.removeEventListener("resize", syncViewportHeight);

      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", syncViewportHeight);
      }
    };
  }, []);

  useEffect(
    function updateDocumentTitle() {
      document.title = getRouteTitle(route);
    },
    [route],
  );

  async function installApp() {
    if (!installPrompt) {
      return;
    }

    installPromptState.value = null;

    try {
      await installPrompt.prompt();
      await installPrompt.userChoice;
    } catch (error) {
      console.warn("[timer] install prompt failed", error);
    }
  }

  return html`
    <${RoutedPage}
      route=${route}
      canInstall=${installPrompt !== null}
      onInstall=${installApp}
    />
  `;
}

/**
 * @param {{ route: RouteState, canInstall: boolean, onInstall: () => void }} props
 * @returns {PreactView}
 */
function RoutedPage(props) {
  const route = props.route;

  if (route.name === "interval-setup") {
    return html`<${IntervalSetupPage} />`;
  }

  if (route.name === "interval-run") {
    return html`<${IntervalRunPage} key=${window.location.search} />`;
  }

  if (route.name === "reaction-setup") {
    return html`<${ReactionSetupPage} />`;
  }

  if (route.name === "reaction-run") {
    return html`<${ReactionRunPage} key=${window.location.search} />`;
  }

  return html`
    <${HomePage} canInstall=${props.canInstall} onInstall=${props.onInstall} />
  `;
}

/**
 * @param {{ canInstall: boolean, onInstall: () => void }} props
 * @returns {PreactView}
 */
function HomePage(props) {
  return html`
    <main class="page-shell">
      <${AppHeader}
        title="Training timers"
        canInstall=${props.canInstall}
        onInstall=${props.onInstall}
      />

      <section class="page-content home-content">
        <div class="page-stack">
          <div class="timer-grid">
            <a
              class="timer-card interval-card stagger-item"
              href="/interval"
              onClick=${handleRouteLinkClick}
            >
              <span class="timer-card-icon">${intervalIcon()}</span>
              <span class="timer-card-copy">
                <strong>Intervals</strong>
                <span>Alternate work and rest for a set number of rounds.</span>
              </span>
              <span class="timer-card-arrow" aria-hidden="true"
                >${arrowIcon()}</span
              >
            </a>

            <a
              class="timer-card reaction-card stagger-item"
              href="/reaction"
              onClick=${handleRouteLinkClick}
            >
              <span class="timer-card-icon">${reactionIcon()}</span>
              <span class="timer-card-copy">
                <strong>Reaction</strong>
                <span>Play an audio cue after a random delay.</span>
              </span>
              <span class="timer-card-arrow" aria-hidden="true"
                >${arrowIcon()}</span
              >
            </a>
          </div>
        </div>
      </section>
    </main>
  `;
}

function IntervalSetupPage() {
  const config = loadIntervalSettings();

  return html`
    <main class="page-shell">
      <${AppHeader}
        backHref="/"
        title="Intervals"
        subtitle="Work, rest, and repetitions"
      />

      <section class="page-content setup-content">
        <div class="setup-stack">
          <form
            class="settings-card stagger-item"
            onSubmit=${startIntervalTimer}
          >
            <${NumberSetting}
              label="Work time"
              hint="Effort phase"
              name="work"
              value=${config.workSeconds}
              minimum=${timerLimits.durationMinimum}
              maximum=${timerLimits.durationMaximum}
              unit="sec"
            />
            <${NumberSetting}
              label="Rest time"
              hint="Recovery phase"
              name="rest"
              value=${config.restSeconds}
              minimum=${timerLimits.durationMinimum}
              maximum=${timerLimits.durationMaximum}
              unit="sec"
            />
            <${NumberSetting}
              label="Repetitions"
              hint="Total work rounds"
              name="repetitions"
              value=${config.repetitions}
              minimum=${timerLimits.repetitionsMinimum}
              maximum=${timerLimits.repetitionsMaximum}
              unit="rounds"
            />
            <${CueSelector}
              idPrefix="interval-work"
              inputName="workCue"
              label="End of work cue"
              hint="Plays when a work section ends"
              selectedKey=${config.workCueSoundKey}
            />
            <${CueSelector}
              idPrefix="interval-rest"
              inputName="restCue"
              label="End of rest cue"
              hint="Plays when a rest section ends"
              selectedKey=${config.restCueSoundKey}
            />

            <button class="primary-button interval-button" type="submit">
              Start ${arrowIcon()}
            </button>
          </form>
        </div>
      </section>
    </main>
  `;
}

function ReactionSetupPage() {
  const config = loadReactionSettings();

  return html`
    <main class="page-shell">
      <${AppHeader}
        backHref="/"
        title="Reaction"
        subtitle="Random audio cue timing"
      />

      <section class="page-content setup-content">
        <div class="setup-stack">
          <form
            class="settings-card stagger-item"
            onSubmit=${startReactionTimer}
          >
            <${NumberSetting}
              label="Minimum wait"
              hint="Earliest possible cue"
              name="minimum"
              value=${config.minimumSeconds}
              minimum=${timerLimits.durationMinimum}
              maximum=${timerLimits.reactionMaximum}
              unit="sec"
              onInput=${clearInputValidity}
            />
            <${NumberSetting}
              label="Maximum wait"
              hint="Latest possible cue"
              name="maximum"
              value=${config.maximumSeconds}
              minimum=${timerLimits.durationMinimum}
              maximum=${timerLimits.reactionMaximum}
              unit="sec"
              onInput=${clearInputValidity}
            />

            <div class="range-note">
              ${soundIcon()}
              <span
                >A short cue repeats at a new random time within this
                range.</span
              >
            </div>
            <${CueSelector}
              idPrefix="reaction"
              inputName="cue"
              label="Cue sound"
              hint="Tap a sound to preview it"
              selectedKey=${config.cueSoundKey}
            />

            <button class="primary-button reaction-button" type="submit">
              Start ${arrowIcon()}
            </button>
          </form>
        </div>
      </section>
    </main>
  `;
}

/**
 * @param {{ label: string, hint: string, name: string, value: number, minimum: number, maximum: number, unit: string, onInput?: (event: Event) => void }} props
 * @returns {PreactView}
 */
function NumberSetting(props) {
  return html`
    <label class="setting-row">
      <span class="setting-copy">
        <strong>${props.label}</strong>
        <small>${props.hint}</small>
      </span>
      <span class="number-control">
        <input
          name=${props.name}
          type="number"
          inputmode="numeric"
          required
          min=${props.minimum}
          max=${props.maximum}
          step="1"
          defaultValue=${props.value}
          onInput=${props.onInput}
          aria-label=${props.label}
        />
        <span>${props.unit}</span>
      </span>
    </label>
  `;
}

/**
 * @param {{ idPrefix: string, inputName: string, label: string, hint: string, selectedKey: CueSoundKey }} props
 * @returns {PreactView}
 */
function CueSelector(props) {
  const optionViews = [];

  for (const option of cueSoundOptions) {
    const inputId = `${props.idPrefix}-cue-${option.key}`;
    optionViews.push(html`
      <label class="cue-option" for=${inputId}>
        <input
          class="cue-radio"
          id=${inputId}
          name=${props.inputName}
          type="radio"
          value=${option.key}
          defaultChecked=${option.key === props.selectedKey}
          onClick=${previewCueSound}
        />
        <span class="cue-option-icon" aria-hidden="true">${soundIcon()}</span>
        <span class="cue-option-copy">
          <strong>${option.label}</strong>
          <small>${option.description}</small>
        </span>
        <span class="cue-option-indicator" aria-hidden="true"></span>
      </label>
    `);
  }

  return html`
    <fieldset class="cue-setting">
      <legend class="setting-copy">
        <strong>${props.label}</strong>
        <small>${props.hint}</small>
      </legend>
      <div class="cue-options">${optionViews}</div>
    </fieldset>
  `;
}

/**
 * @param {Event} event
 * @returns {void}
 */
function previewCueSound(event) {
  const input = event.currentTarget;
  if (!(input instanceof HTMLInputElement)) {
    return;
  }

  if (!input.checked) {
    return;
  }

  void playCueSound(input.value);
}

function IntervalRunPage() {
  const params = new URLSearchParams(window.location.search);
  const config = getIntervalConfigFromParams(params);
  const legacyCueSoundKey = params.get("cue");
  const workCueSoundKey = normalizeCueSoundKey(
    params.get("workCue") || legacyCueSoundKey,
  );
  const restCueSoundKey = normalizeCueSoundKey(
    params.get("restCue") || legacyCueSoundKey,
  );
  const totalSeconds = getIntervalTotalSeconds(config);
  const [session, setSession] = useState(createIntervalSession);
  const isRunning = session.status === "running";
  const now = useTimerClock(isRunning);
  const elapsedSeconds = getIntervalElapsedSeconds(session, now);
  const snapshot = getIntervalSnapshot(config, elapsedSeconds);
  const previousPhaseKey = useRef(snapshot.key);
  const sessionComplete = session.status === "complete";
  const screenClass = getIntervalRunClass(snapshot.phase, session.status);
  const phaseLabel = getIntervalPhaseLabel(snapshot.phase, session.status);
  const progressStyle = { width: `${snapshot.progress * 100}%` };

  useSessionBusy(!sessionComplete);
  useScreenWakeLock(isRunning);

  useEffect(
    function announcePhaseChange() {
      if (previousPhaseKey.current === snapshot.key) {
        return;
      }

      previousPhaseKey.current = snapshot.key;
      const cueSoundKey =
        snapshot.phase === "work" ? restCueSoundKey : workCueSoundKey;
      void playRepeatedCueSound(cueSoundKey);
    },
    [restCueSoundKey, snapshot.key, workCueSoundKey],
  );

  useEffect(
    function completeSession() {
      if (snapshot.phase !== "complete") {
        return;
      }

      if (session.status === "complete") {
        return;
      }

      setSession({
        status: "complete",
        startedAt: session.startedAt,
        elapsedSeconds: totalSeconds,
      });
    },
    [session.startedAt, session.status, snapshot.phase, totalSeconds],
  );

  function togglePause() {
    const actionNow = performance.now();

    if (session.status === "paused") {
      enableWakeLock();
    }

    setSession(function updateSession(previousSession) {
      if (previousSession.status === "paused") {
        return {
          status: "running",
          startedAt: actionNow,
          elapsedSeconds: previousSession.elapsedSeconds,
        };
      }

      if (previousSession.status !== "running") {
        return previousSession;
      }

      const elapsed = getIntervalElapsedSeconds(previousSession, actionNow);
      return {
        status: "paused",
        startedAt: actionNow,
        elapsedSeconds: elapsed,
      };
    });
  }

  function runAgain() {
    enableWakeLock();
    setSession(createIntervalSession());
  }

  return html`
    <main class=${screenClass}>
      <${RunHeader}
        label="Intervals"
        meta=${`Round ${snapshot.round} / ${config.repetitions}`}
        fallback="/interval"
      />

      <section class="run-stage" aria-live="polite" aria-atomic="true">
        <p class="run-kicker">
          ${snapshot.phase === "complete"
            ? `${config.repetitions} rounds complete`
            : `Round ${snapshot.round} of ${config.repetitions}`}
        </p>
        <h1>${phaseLabel}</h1>
        <output class="timer-readout">
          ${snapshot.phase === "complete"
            ? "DONE"
            : formatClock(snapshot.remainingSeconds)}
        </output>
        <div class="progress-track" aria-hidden="true">
          <span style=${progressStyle}></span>
        </div>
      </section>

      <footer class="run-controls">
        ${sessionComplete
          ? html`
              <button class="run-primary" type="button" onClick=${runAgain}>
                Run again
              </button>
              <button
                class="run-secondary"
                type="button"
                onClick=${function finishInterval() {
                  goBackOr("/interval");
                }}
              >
                Done
              </button>
            `
          : html`
              <button class="run-primary" type="button" onClick=${togglePause}>
                ${session.status === "paused" ? playIcon() : pauseIcon()}
                ${session.status === "paused" ? "Resume" : "Pause"}
              </button>
              <button
                class="run-secondary"
                type="button"
                onClick=${function endInterval() {
                  goBackOr("/interval");
                }}
              >
                End session
              </button>
            `}
      </footer>
    </main>
  `;
}

function ReactionRunPage() {
  const params = new URLSearchParams(window.location.search);
  const config = getReactionConfigFromParams(params);
  const cueSoundKey = normalizeCueSoundKey(params.get("cue"));
  const [session, setSession] = useState(function createInitialSession() {
    return createReactionSession(config);
  });
  const isRunning = session.status === "running";
  const now = useTimerClock(isRunning);
  const cueIsVisible = isRunning && now < session.cueVisibleUntil;
  const cueNumber = cueIsVisible ? session.cueCount : session.cueCount + 1;
  const screenClass = getReactionRunClass(session.status, cueIsVisible);
  const statusLabel = getReactionStatusLabel(session.status, cueIsVisible);

  useSessionBusy(true);
  useScreenWakeLock(isRunning);

  useEffect(
    function fireReactionCue() {
      if (!isRunning) {
        return;
      }

      if (now < session.targetAt) {
        return;
      }

      const delaySeconds = getRandomDelaySeconds(
        config.minimumSeconds,
        config.maximumSeconds,
      );
      const nextTargetAt = now + delaySeconds * 1000;

      void playCueSound(cueSoundKey);
      setSession(function advanceCue(previousSession) {
        if (previousSession.targetAt !== session.targetAt) {
          return previousSession;
        }

        return {
          status: "running",
          targetAt: nextTargetAt,
          remainingWhenPaused: 0,
          cueVisibleUntil: now + reactionCueVisibleMs,
          cueCount: previousSession.cueCount + 1,
        };
      });
    },
    [
      config.maximumSeconds,
      config.minimumSeconds,
      cueSoundKey,
      isRunning,
      now,
      session.targetAt,
    ],
  );

  function togglePause() {
    const actionNow = performance.now();

    if (session.status === "paused") {
      enableWakeLock();
    }

    setSession(function updateSession(previousSession) {
      if (previousSession.status === "paused") {
        return {
          status: "running",
          targetAt: actionNow + previousSession.remainingWhenPaused,
          remainingWhenPaused: 0,
          cueVisibleUntil: 0,
          cueCount: previousSession.cueCount,
        };
      }

      const remainingWhenPaused = Math.max(
        0,
        previousSession.targetAt - actionNow,
      );
      return {
        status: "paused",
        targetAt: previousSession.targetAt,
        remainingWhenPaused,
        cueVisibleUntil: 0,
        cueCount: previousSession.cueCount,
      };
    });
  }

  return html`
    <main class=${screenClass}>
      <${RunHeader}
        label="Reaction"
        meta=${`${session.cueCount} ${session.cueCount === 1 ? "cue" : "cues"}`}
        fallback="/reaction"
      />

      <section
        class="run-stage reaction-stage"
        role="status"
        aria-live="assertive"
        aria-atomic="true"
      >
        <div class="reaction-orbit" aria-hidden="true">
          <span></span>
          <span></span>
        </div>
        <p class="run-kicker">Cue ${cueNumber}</p>
        <h1>${statusLabel}</h1>
        <p class="reaction-instruction">
          ${session.status === "paused"
            ? "The next cue is held."
            : cueIsVisible
              ? "React now."
              : "Stay ready. The countdown is hidden."}
        </p>
      </section>

      <footer class="run-controls">
        <button class="run-primary" type="button" onClick=${togglePause}>
          ${session.status === "paused" ? playIcon() : pauseIcon()}
          ${session.status === "paused" ? "Resume" : "Pause"}
        </button>
        <button
          class="run-secondary"
          type="button"
          onClick=${function endReaction() {
            goBackOr("/reaction");
          }}
        >
          End session
        </button>
      </footer>
    </main>
  `;
}

/**
 * @param {{ title: string, subtitle?: string, backHref?: string, canInstall?: boolean, onInstall?: () => void }} props
 * @returns {PreactView}
 */
function AppHeader(props) {
  return html`
    <header class="app-header">
      <div class="header-leading">
        ${props.backHref
          ? html`
              <button
                class="back-button"
                type="button"
                onClick=${function goBack() {
                  goBackOr(props.backHref || "/");
                }}
                aria-label="Go back"
              >
                ${backIcon()}
              </button>
            `
          : html`<span class="brand-mark" aria-hidden="true">TT</span>`}
        <div class="header-title" onClick=${handleDebugTap}>
          <strong>${props.title}</strong>
          ${props.subtitle ? html`<span>${props.subtitle}</span>` : ""}
        </div>
      </div>

      ${props.canInstall && props.onInstall
        ? html`
            <button
              class="install-button"
              type="button"
              onClick=${props.onInstall}
            >
              Install
            </button>
          `
        : ""}
    </header>
  `;
}

/**
 * @param {{ label: string, meta: string, fallback: string }} props
 * @returns {PreactView}
 */
function RunHeader(props) {
  return html`
    <header class="run-header">
      <button
        class="run-back-button"
        type="button"
        onClick=${function leaveRunScreen() {
          goBackOr(props.fallback);
        }}
      >
        ${backIcon()} <span>End</span>
      </button>
      <strong>${props.label}</strong>
      <span class="run-meta tabular-nums">${props.meta}</span>
    </header>
  `;
}

/**
 * @param {Event} event
 * @returns {void}
 */
function startIntervalTimer(event) {
  event.preventDefault();
  const form = event.currentTarget;
  if (!(form instanceof HTMLFormElement)) {
    return;
  }

  const formData = new FormData(form);
  const params = new URLSearchParams();
  params.set("work", String(formData.get("work") || ""));
  params.set("rest", String(formData.get("rest") || ""));
  params.set("repetitions", String(formData.get("repetitions") || ""));
  const config = getIntervalConfigFromParams(params);
  const workCueSoundKey = normalizeCueSoundKey(formData.get("workCue"));
  const restCueSoundKey = normalizeCueSoundKey(formData.get("restCue"));
  const settings = { ...config, workCueSoundKey, restCueSoundKey };
  const routeParams = new URLSearchParams({
    work: String(config.workSeconds),
    rest: String(config.restSeconds),
    repetitions: String(config.repetitions),
    workCue: workCueSoundKey,
    restCue: restCueSoundKey,
  });

  saveIntervalSettings(settings);
  enableWakeLock();
  void prepareCueSound(workCueSoundKey);
  void prepareCueSound(restCueSoundKey);
  navigateTo(`/interval/run?${routeParams.toString()}`);
}

/**
 * @param {Event} event
 * @returns {void}
 */
function startReactionTimer(event) {
  event.preventDefault();
  const form = event.currentTarget;
  if (!(form instanceof HTMLFormElement)) {
    return;
  }

  const minimumInput = getFormInput(form, "minimum");
  const maximumInput = getFormInput(form, "maximum");
  if (!minimumInput || !maximumInput) {
    return;
  }

  const minimumSeconds = Number(minimumInput.value);
  const maximumSeconds = Number(maximumInput.value);
  if (maximumSeconds < minimumSeconds) {
    maximumInput.setCustomValidity(
      "Maximum wait must be equal to or greater than minimum wait.",
    );
    maximumInput.reportValidity();
    return;
  }

  const params = new URLSearchParams({
    minimum: minimumInput.value,
    maximum: maximumInput.value,
  });
  const config = getReactionConfigFromParams(params);
  const formData = new FormData(form);
  const cueSoundKey = normalizeCueSoundKey(formData.get("cue"));
  const settings = { ...config, cueSoundKey };
  const routeParams = new URLSearchParams({
    minimum: String(config.minimumSeconds),
    maximum: String(config.maximumSeconds),
    cue: cueSoundKey,
  });

  saveReactionSettings(settings);
  enableWakeLock();
  void prepareCueSound(cueSoundKey);
  navigateTo(`/reaction/run?${routeParams.toString()}`);
}

/**
 * @param {Event} event
 * @returns {void}
 */
function clearInputValidity(event) {
  const input = event.currentTarget;
  if (!(input instanceof HTMLInputElement)) {
    return;
  }

  input.setCustomValidity("");

  if (!input.form) {
    return;
  }

  const maximumInput = getFormInput(input.form, "maximum");
  if (maximumInput) {
    maximumInput.setCustomValidity("");
  }
}

/**
 * @param {HTMLFormElement} form
 * @param {string} name
 * @returns {HTMLInputElement | null}
 */
function getFormInput(form, name) {
  const field = form.elements.namedItem(name);
  if (field instanceof HTMLInputElement) {
    return field;
  }

  return null;
}

/**
 * @returns {IntervalSession}
 */
function createIntervalSession() {
  return {
    status: "running",
    startedAt: performance.now(),
    elapsedSeconds: 0,
  };
}

/**
 * @param {IntervalSession} session
 * @param {number} now
 * @returns {number}
 */
function getIntervalElapsedSeconds(session, now) {
  let elapsedSeconds = session.elapsedSeconds;

  if (session.status === "running") {
    const activeElapsed = Math.max(0, now - session.startedAt) / 1000;
    elapsedSeconds += activeElapsed;
  }

  return elapsedSeconds;
}

/**
 * @param {{ minimumSeconds: number, maximumSeconds: number }} config
 * @returns {ReactionSession}
 */
function createReactionSession(config) {
  const delaySeconds = getRandomDelaySeconds(
    config.minimumSeconds,
    config.maximumSeconds,
  );

  return {
    status: "running",
    targetAt: performance.now() + delaySeconds * 1000,
    remainingWhenPaused: 0,
    cueVisibleUntil: 0,
    cueCount: 0,
  };
}

/**
 * @param {boolean} isRunning
 * @returns {number}
 */
function useTimerClock(isRunning) {
  const [now, setNow] = useState(performance.now());

  useEffect(
    function runClock() {
      if (!isRunning) {
        return;
      }

      let animationFrameId = 0;
      let lastPaintAt = performance.now();

      /** @param {number} frameNow */
      function updateClock(frameNow) {
        const paintIsDue = frameNow - lastPaintAt >= clockFrameIntervalMs;
        if (paintIsDue) {
          lastPaintAt = frameNow;
          setNow(frameNow);
        }

        animationFrameId = window.requestAnimationFrame(updateClock);
      }

      animationFrameId = window.requestAnimationFrame(updateClock);

      return function stopClock() {
        window.cancelAnimationFrame(animationFrameId);
      };
    },
    [isRunning],
  );

  return now;
}

/**
 * Keep the shared busy flag in sync before a new screen mounts.
 * @param {boolean} isBusy
 * @returns {void}
 */
function useSessionBusy(isBusy) {
  useLayoutEffect(
    function updateBusyState() {
      appBusy = isBusy;

      return function clearBusyState() {
        appBusy = false;
      };
    },
    [isBusy],
  );
}

/**
 * Release the old screen's lock before the next screen acquires it.
 * @param {boolean} enabled
 * @returns {void}
 */
function useScreenWakeLock(enabled) {
  useLayoutEffect(
    function manageWakeLock() {
      if (!enabled) {
        disableWakeLock();
        return;
      }

      return bindWakeLockLifecycle();
    },
    [enabled],
  );
}

/**
 * @returns {IntervalSettings}
 */
function loadIntervalSettings() {
  const defaults = {
    ...intervalDefaults,
    workCueSoundKey: defaultCueSoundKey,
    restCueSoundKey: defaultCueSoundKey,
  };
  const rawValue = localStorage.getItem(intervalStorageKey);
  if (!rawValue) {
    return defaults;
  }

  try {
    const saved =
      /** @type {{ workSeconds?: unknown, restSeconds?: unknown, repetitions?: unknown, cueSoundKey?: unknown, workCueSoundKey?: unknown, restCueSoundKey?: unknown }} */ (
        JSON.parse(rawValue)
      );
    const params = new URLSearchParams({
      work: String(saved.workSeconds || ""),
      rest: String(saved.restSeconds || ""),
      repetitions: String(saved.repetitions || ""),
    });
    const config = getIntervalConfigFromParams(params);
    const legacyCueSoundKey = normalizeCueSoundKey(saved.cueSoundKey);
    const workCueSoundKey = normalizeCueSoundKey(
      saved.workCueSoundKey || legacyCueSoundKey,
    );
    const restCueSoundKey = normalizeCueSoundKey(
      saved.restCueSoundKey || legacyCueSoundKey,
    );
    return { ...config, workCueSoundKey, restCueSoundKey };
  } catch (error) {
    console.warn("[timer] interval settings ignored", error);
  }

  return defaults;
}

/**
 * @param {IntervalSettings} config
 * @returns {void}
 */
function saveIntervalSettings(config) {
  localStorage.setItem(intervalStorageKey, JSON.stringify(config));
}

/**
 * @returns {ReactionSettings}
 */
function loadReactionSettings() {
  const defaults = {
    ...reactionDefaults,
    cueSoundKey: defaultCueSoundKey,
  };
  const rawValue = localStorage.getItem(reactionStorageKey);
  if (!rawValue) {
    return defaults;
  }

  try {
    const saved =
      /** @type {{ minimumSeconds?: unknown, maximumSeconds?: unknown, cueSoundKey?: unknown }} */ (
        JSON.parse(rawValue)
      );
    const params = new URLSearchParams({
      minimum: String(saved.minimumSeconds || ""),
      maximum: String(saved.maximumSeconds || ""),
    });
    const config = getReactionConfigFromParams(params);
    const cueSoundKey = normalizeCueSoundKey(saved.cueSoundKey);
    return { ...config, cueSoundKey };
  } catch (error) {
    console.warn("[timer] reaction settings ignored", error);
  }

  return defaults;
}

/**
 * @param {ReactionSettings} config
 * @returns {void}
 */
function saveReactionSettings(config) {
  localStorage.setItem(reactionStorageKey, JSON.stringify(config));
}

/**
 * @param {MouseEvent} event
 * @returns {void}
 */
function handleRouteLinkClick(event) {
  if (event.defaultPrevented || event.button !== 0) {
    return;
  }

  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return;
  }

  const anchor = event.currentTarget;
  if (!(anchor instanceof HTMLAnchorElement)) {
    return;
  }

  event.preventDefault();
  const url = new URL(anchor.href);
  navigateTo(`${url.pathname}${url.search}`);
}

/**
 * @param {string} route
 * @param {boolean} replace
 * @returns {void}
 */
function navigateTo(route, replace = false) {
  const currentDepth = getHistoryDepth();
  const nextDepth = replace ? currentDepth : currentDepth + 1;
  const state = { [historyDepthKey]: nextDepth };

  if (replace) {
    window.history.replaceState(state, "", route);
  } else {
    window.history.pushState(state, "", route);
  }

  window.dispatchEvent(new Event(routeEventName));
}

/**
 * @param {string} fallback
 * @returns {void}
 */
function goBackOr(fallback) {
  if (getHistoryDepth() > 0) {
    window.history.back();
    return;
  }

  navigateTo(fallback, true);
}

function initializeHistoryState() {
  const state = window.history.state;
  if (state && typeof state[historyDepthKey] === "number") {
    return;
  }

  window.history.replaceState(
    { [historyDepthKey]: 0 },
    "",
    window.location.href,
  );
}

/**
 * @returns {number}
 */
function getHistoryDepth() {
  const state = window.history.state;
  let depth = 0;

  if (state && typeof state[historyDepthKey] === "number") {
    depth = Math.max(0, state[historyDepthKey]);
  }

  return depth;
}

/**
 * @returns {RouteState}
 */
function getRouteStateFromLocation() {
  const trimmedPath = window.location.pathname.replace(/\/+$/, "");
  const path = trimmedPath || "/";
  let name = /** @type {RouteState["name"]} */ ("home");

  if (path === "/interval") {
    name = "interval-setup";
  }

  if (path === "/interval/run") {
    name = "interval-run";
  }

  if (path === "/reaction") {
    name = "reaction-setup";
  }

  if (path === "/reaction/run") {
    name = "reaction-run";
  }

  return { name };
}

/**
 * @param {RouteState} route
 * @returns {string}
 */
function getRouteTitle(route) {
  let title = "Training Timers";

  if (route.name === "interval-setup") {
    title = "Intervals · Training Timers";
  }

  if (route.name === "interval-run") {
    title = "Interval Session · Training Timers";
  }

  if (route.name === "reaction-setup") {
    title = "Reaction · Training Timers";
  }

  if (route.name === "reaction-run") {
    title = "Reaction Session · Training Timers";
  }

  return title;
}

function syncViewportHeight() {
  const isStandalone = isStandalonePwa();
  const appHeight = getAppHeightValue(isStandalone);
  const documentRoot = document.documentElement;

  documentRoot.style.setProperty("--app-height", appHeight);

  if (isStandalone) {
    documentRoot.style.setProperty("--app-safe-bottom", "0px");
    return;
  }

  documentRoot.style.removeProperty("--app-safe-bottom");
}

/**
 * @param {boolean} isStandalone
 * @returns {string}
 */
function getAppHeightValue(isStandalone) {
  if (isStandalone) {
    return "100vh";
  }

  let viewportHeight = window.innerHeight;
  if (window.visualViewport) {
    viewportHeight = window.visualViewport.height;
  }

  return `${Math.round(viewportHeight)}px`;
}

/**
 * @returns {boolean}
 */
function isStandalonePwa() {
  const navigatorWithStandalone =
    /** @type {Navigator & { standalone?: boolean }} */ (navigator);
  const displayModeStandalone = window.matchMedia(
    "(display-mode: standalone)",
  ).matches;
  return displayModeStandalone || navigatorWithStandalone.standalone === true;
}

/**
 * @returns {boolean}
 */
function isAppIdle() {
  return !appBusy;
}

async function handleDebugTap() {
  const now = Date.now();
  const withinWindow = now - debugTapState.lastTapAt <= debugTapWindowMs;
  debugTapState.count = withinWindow ? debugTapState.count + 1 : 1;
  debugTapState.lastTapAt = now;

  if (debugTapState.count < debugTapThreshold) {
    return;
  }

  debugTapState.count = 0;
  await showDebugInfo();
}

async function showDebugInfo() {
  const cacheNames = await getAppCacheNames();
  const serviceWorkerState = await getServiceWorkerState();
  const wakeLockState = await getWakeLockDebugState();
  const lines = [
    `Online: ${navigator.onLine}`,
    `PWA standalone: ${isStandalonePwa()}`,
    `Session busy: ${appBusy}`,
    `SW: ${serviceWorkerState}`,
    `Wake lock API: ${"wakeLock" in navigator}`,
    `Wake lock permission: ${wakeLockState.permission}`,
    `Wake lock requested: ${wakeLockState.requested}`,
    `Wake lock native: ${wakeLockState.nativeActive}`,
    `Wake lock pending: ${wakeLockState.requestPending}`,
    `Wake lock video: ${wakeLockState.videoFallback}`,
    `Caches: ${cacheNames.join(", ") || "(none)"}`,
    `URL: ${window.location.href}`,
    `UA: ${navigator.userAgent}`,
  ];

  window.alert(lines.join("\n"));
}

/**
 * @returns {Promise<string[]>}
 */
async function getAppCacheNames() {
  let cacheNames = [];

  try {
    const allCacheNames = await caches.keys();
    for (let i = 0, total = allCacheNames.length; i < total; i += 1) {
      const cacheName = allCacheNames[i];
      if (cacheName.startsWith(cachePrefix)) {
        cacheNames.push(cacheName);
      }
    }
  } catch (error) {
    cacheNames = [`(cache inspection failed: ${getErrorMessage(error)})`];
  }

  return cacheNames;
}

/**
 * @returns {Promise<string>}
 */
async function getServiceWorkerState() {
  let state = "(unsupported)";

  try {
    if ("serviceWorker" in navigator) {
      const registration = await navigator.serviceWorker.getRegistration();
      state = getRegistrationState(registration);
    }
  } catch (error) {
    state = `(registration failed: ${getErrorMessage(error)})`;
  }

  return state;
}

/**
 * @param {ServiceWorkerRegistration | undefined} registration
 * @returns {string}
 */
function getRegistrationState(registration) {
  let state = "(no registration)";

  if (registration) {
    const active = registration.active ? registration.active.state : "none";
    const waiting = registration.waiting ? registration.waiting.state : "none";
    const installing = registration.installing
      ? registration.installing.state
      : "none";
    state = `active=${active} waiting=${waiting} installing=${installing}`;
  }

  return state;
}

/**
 * @param {unknown} error
 * @returns {string}
 */
function getErrorMessage(error) {
  let message = String(error);

  if (error instanceof Error) {
    message = error.message;
  }

  return message;
}

/**
 * @param {"work" | "rest" | "complete"} phase
 * @param {IntervalSession["status"]} status
 * @returns {string}
 */
function getIntervalRunClass(phase, status) {
  let className = "run-shell interval-run work-phase";

  if (phase === "rest") {
    className = "run-shell interval-run rest-phase";
  }

  if (phase === "complete") {
    className = "run-shell interval-run complete-phase";
  }

  if (status === "paused") {
    className += " paused-phase";
  }

  return className;
}

/**
 * @param {"work" | "rest" | "complete"} phase
 * @param {IntervalSession["status"]} status
 * @returns {string}
 */
function getIntervalPhaseLabel(phase, status) {
  let label = "Work";

  if (phase === "rest") {
    label = "Rest";
  }

  if (phase === "complete") {
    label = "Complete";
  }

  if (status === "paused") {
    label = "Paused";
  }

  return label;
}

/**
 * @param {ReactionSession["status"]} status
 * @param {boolean} cueIsVisible
 * @returns {string}
 */
function getReactionRunClass(status, cueIsVisible) {
  let className = "run-shell reaction-run waiting-phase";

  if (cueIsVisible) {
    className = "run-shell reaction-run cue-phase";
  }

  if (status === "paused") {
    className = "run-shell reaction-run paused-reaction-phase";
  }

  return className;
}

/**
 * @param {ReactionSession["status"]} status
 * @param {boolean} cueIsVisible
 * @returns {string}
 */
function getReactionStatusLabel(status, cueIsVisible) {
  let label = "Wait";

  if (cueIsVisible) {
    label = "Go";
  }

  if (status === "paused") {
    label = "Paused";
  }

  return label;
}

/** @returns {PreactView} */
function intervalIcon() {
  return html`
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 7h10M4 12h16M4 17h7" />
      <path d="m17 5 3 2-3 2M14 15l-3 2 3 2" />
    </svg>
  `;
}

/** @returns {PreactView} */
function reactionIcon() {
  return html`
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 4a8 8 0 1 0 8 8" />
      <path d="M12 8a4 4 0 1 0 4 4M12 12l7-7" />
      <path d="M16 5h3v3" />
    </svg>
  `;
}

/** @returns {PreactView} */
function soundIcon() {
  return html`
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M11 6 7 9H4v6h3l4 3V6Z" />
      <path d="M15 9.5a4 4 0 0 1 0 5M18 7a7 7 0 0 1 0 10" />
    </svg>
  `;
}

/** @returns {PreactView} */
function arrowIcon() {
  return html`
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 12h14M14 7l5 5-5 5" />
    </svg>
  `;
}

/** @returns {PreactView} */
function backIcon() {
  return html`
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m15 18-6-6 6-6" />
    </svg>
  `;
}

/** @returns {PreactView} */
function pauseIcon() {
  return html`
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M9 7v10M15 7v10" />
    </svg>
  `;
}

/** @returns {PreactView} */
function playIcon() {
  return html`
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m9 7 8 5-8 5V7Z" />
    </svg>
  `;
}

main();
